import React, { useEffect, useState } from "react";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { TablePaginationConfig } from "antd/es/table/interface";
import { LIMIT } from "../../constants/pagination.constant";
import Loading from "../shared/loading/loading";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { GuideFile } from "../../interfaces/guides.interface";
import {
  deleteGuide,
  downloadGuideFile,
  getAllGuides,
} from "../../services/guides.service";
import { useRole } from "../../hooks/user-roles";
import { Popconfirm } from "antd/lib";
import Message from "../shared/Message/Message";
import { AxiosError } from "axios";
import { DeleteOutlined } from "@ant-design/icons";
dayjs.extend(customParseFormat);

interface DataType extends GuideFile {
  key: React.Key;
  dateFormatted: string;
  position: number;
}

interface FilesDataType extends DataType {
  key: React.Key;
}
let columns: ColumnsType<FilesDataType> = [
  {
    title: "Description",
    dataIndex: "description",
    sorter: (a, b) => a.description.localeCompare(b.description),
    sortDirections: ["descend"],
  },
  {
    title: "Created At",
    dataIndex: "dateFormatted",
    sorter: (a, b) => a.dateFormatted.localeCompare(b.dateFormatted),
    sortDirections: ["descend"],
  },
  {
    title: "Filename",
    dataIndex: "name",
    sorter: (a, b) => a.name.localeCompare(b.name),
    sortDirections: ["descend"],
    onCell: (record) => {
      return {
        onClick: () => {
          downloadGuideFile(record.id, record.name ?? record.url);
        },
        style: { cursor: "pointer" },
      };
    },
  },
];

interface TableParams {
  pagination?: TablePaginationConfig;
}

interface GuidesTableProps {
  query?: string;
}

const StaffGuidesTable = ({ query }: GuidesTableProps) => {
  const queryClient = useQueryClient();
  const { isAdmin } = useRole();
  const [cols, setCols] = useState<ColumnsType<FilesDataType>>(columns);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: LIMIT,
    },
  });
  const limit = tableParams.pagination?.pageSize ?? LIMIT;
  const skip = ((tableParams.pagination?.current ?? 1) - 1) * limit;
  const mutationOnSuccess = () => {
    Message().success("Guide Deleted!");
    queryClient.invalidateQueries({
      queryKey: [`guides-files`],
    });
  };
  const mutationOnError = (response: AxiosError) => {
    Message().success(response.message);
  };

  const deleteGuideMutation = useMutation({
    mutationFn: (guideId: number) => {
      return deleteGuide(guideId);
    },
    onSuccess: mutationOnSuccess,
    onError: mutationOnError,
  });
  const { isPending, data } = useQuery({
    queryKey: [`guides-files`],
    queryFn: async () => {
      const result = await getAllGuides({
        limit,
        skip,
        query,
      });
      if (result.count) {
        setTableParams((current) => ({
          pagination: { ...current.pagination, total: result.count },
        }));
      }
      return result;
    },
    placeholderData: keepPreviousData,
    select: ({ list, count }) => ({
      count,
      list: list.map((guide: GuideFile, index: number) => {
        return {
          ...guide,
          key: guide.id,
          position: index + 1,
          dateFormatted: dayjs(guide.createdAt, "YYYY-MM-DD").format(
            "DD-MMM-YYYY",
          ),
        };
      }),
    }),
  });
  const handleTableChange = (pagination: TablePaginationConfig) => {
    setTableParams({
      pagination,
    });
  };
  useEffect(() => {
    if (isAdmin) {
      setCols([
        ...columns,
        {
          title: "Actions",
          render: (_: any, record: FilesDataType) => (
            <Popconfirm
              title="Sure to delete?"
              onConfirm={() => {
                deleteGuideMutation.mutate(record.id);
              }}
            >
              <DeleteOutlined className="error" />
            </Popconfirm>
          ),
        },
      ]);
    }
  }, [isAdmin]);

  if (isPending) {
    return <Loading></Loading>;
  }

  return (
    <div className="p-4">
      <Table
        columns={cols}
        dataSource={data?.list}
        bordered={true}
        onChange={handleTableChange}
        pagination={
          data?.count ? data?.count > LIMIT && tableParams.pagination : false
        }
      />
    </div>
  );
};
export default StaffGuidesTable;
